import { FC, Fragment, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, Gift, Info } from 'react-feather';
import { useMutation, useQuery } from '@apollo/client';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Card, Flex, Spacer } from '../../../components/shared';
import { GET_NOTIFICATIONS, MARK_NOTIFICATIONS_AS_READ } from '../../../graphql';
import { useRouter } from '../../../hooks';
import { PageWrap } from '../../../layouts';
import { convertToDuration } from '../../../utils/dates';
import { getNotificationsList } from '../../../utils/idbFns';
import { SCREEN } from '../../../theme/breakpoints';

const isWeb = SCREEN.isWeb;

type NotificationNode = {
  id: string;
  createdAt: string;
  institutionId: string;
  registrationId: string;
  monitoringFormId: string;
  dueDate: string;
  message: string;
  startDate: string;
  title: string;
  topic: string;
  notificationType: string;
  read: boolean;
};

type NotificationEdge = {
  node: NotificationNode;
};

type NotificationsProps = {};

const PseudoLink = styled.div`
  text-decoration: underline;
`;

const NotificationPageWrap = styled(PageWrap as any)`
  font-size: 11px;
  // height: calc(100vh - 75pt);
  width: 100%;
  overflow: auto;
  // height: ${isWeb ? 'calc(100vh - 75pt)' : '100%'};
  // left: ${isWeb ? '50%' : '0'};
`;

const Title = styled.span`
  display: flex;
  font-size: 12px;
  font-weight: 600;
`;

const Paragraph = styled.span`
  color: ${themeGet('colors.putty', '#6D6D6D')};
  display: flex;
  font-size: 11px;
  height: 30px;
  margin-top: 7px;
`;

const Duration = styled.span`
  color: ${themeGet('colors.putty', '#6D6D6D')};
  display: flex;
  font-size: 10px;
  margin-top: 7px;
`;

const UnreadMarker = styled.div`
  background-color: #e02020;
  border: none;
  border-radius: 10px;
  height: 20px;
  position: relative;
  right: -10px;
  top: -10px;
  width: 20px;
`;

const NoNotifications = styled.div`
  height: 100%;
  position: relative;
  top: 10%;
  color: ${isWeb ? 'white' : 'black'};
`;

const Notifications: FC<NotificationsProps> = props => {
  const { history } = useRouter();
  const [markNotificationsAsRead] = useMutation(MARK_NOTIFICATIONS_AS_READ);
  const [notifications, setNotifications] = useState<[{ id: string }] | any>([]);
  const { data, error } = useQuery(GET_NOTIFICATIONS, { variables: { input: { first: 10 } } });

  const handleNotificationClick = async ({ node }: NotificationEdge) => {
    !node.read && (await markNotificationsAsRead({ variables: { notificationIds: [node.id] } }));
    // construct the redirect url
    var redirectURL: string;
    if (node.monitoringFormId) {
      const date = new Date(node.dueDate);
      redirectURL = `/my-forms/${node.monitoringFormId}/${date.getTime()}/capture`;
    } else {
      const registrationSubURL = node.registrationId ? `/registrations/${node.registrationId}/application` : '/';
      redirectURL = `/institution/manage/institutions/${node.institutionId}${registrationSubURL}`;
    }

    history.push(redirectURL);
  };

  useEffect(() => {
    if (data && data.notifications && data.notifications.edges) {
      setNotifications(data.notifications.edges);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      getNotificationsList().then(list => setNotifications(list));
    }
  }, [error]);

  const handleMarkAllAsRead = async () => {
    const allNotificationIds = notifications && notifications.map((notif: NotificationEdge) => notif.node.id);
    await markNotificationsAsRead({ variables: { notificationIds: allNotificationIds } });
  };

  const renderIcon = (notificationType: string) => {
    if (notificationType === 'WARNING') return <AlertCircle color="#FFF" fill="#E02020" />;
    if (notificationType === 'SUCCESS') return <CheckCircle color="#FFF" fill="#6DD500" />;
    if (notificationType === 'INFO') return <Info color="#FFF" fill="#0091FF" />;

    return null;
  };

  const hasNotifications = !!(notifications && notifications.length);

  return hasNotifications ? (
    <NotificationPageWrap flexDirection="column" p="4">
      <Flex direction="row" size="1" maxHeight="30px" width="100%">
        <PseudoLink onClick={handleMarkAllAsRead}>Mark all as read</PseudoLink>
      </Flex>

      <Flex direction="column" size="1" justifyContent="flex-start" width="100%">
        {notifications &&
          notifications.map(({ node }: NotificationEdge) => (
            <Fragment key={node.id}>
              <Card maxHeight="110px" onClick={() => handleNotificationClick({ node })}>
                <Flex direction="row" size="1" width="100%">
                  <Flex direction="column" size="0.2">
                    {renderIcon(node.notificationType)}
                  </Flex>
                  <Flex direction="column" size="1" justifyContent="flex-start">
                    <Title>{node.title}</Title>
                    <Paragraph>{node.message}</Paragraph>
                    <Duration>{convertToDuration(node.startDate)}</Duration>
                  </Flex>
                  <Flex direction="column" size="0.05">
                    {!node.read && <UnreadMarker />}
                  </Flex>
                </Flex>
              </Card>
              <Spacer spacing={15} />
            </Fragment>
          ))}
      </Flex>
    </NotificationPageWrap>
  ) : (
    <NoNotifications>
      <Flex direction="column" size="1" alignItems="center">
        <Gift />
        <p
          {...(isWeb && {
            color: 'white',
          })}
        >
          You have no notifications!
        </p>
      </Flex>
    </NoNotifications>
  );
};

export default Notifications;
